import React, { useState, useMemo, useEffect } from 'react';
import { Table, Button, Alert, Popconfirm, Tooltip, Icon, Input, Checkbox } from 'antd';
import {
  canDeleteInviteRow,
  canEditAssignment,
  canSelectInviteRow,
  getInviteEmail,
  getInviteeBackendStatus,
  getPendingRowKey,
  getSendableInviteTargets,
  getSendInviteButtonLabel,
  formatInviteeStatusLabel,
  isInvitableRow,
} from '../../../../../../Onboarding/pendingInviteWorkflow';
import HelpTip from './HelpTip';
import { ONBOARDING_HELP } from '../brokerageSubscriptionHelp';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const PLACEHOLDER = '—';

const toDisplayFilters = (arr) =>
  [...new Set(arr.map((v) => (v != null && v !== '' ? String(v) : PLACEHOLDER)))]
    .sort()
    .map((v) => ({ text: v, value: v }));

const getEmailColumnValue = (record) => getInviteEmail(record) || PLACEHOLDER;
const getCellColumnValue = (value) => (value != null && value !== '' ? String(value) : PLACEHOLDER);

const getStatusColumnValue = (record) => {
  const label = formatInviteeStatusLabel(getInviteeBackendStatus(record));
  return label && label !== PLACEHOLDER ? label : PLACEHOLDER;
};

const getFileColumnValue = (record) => record.fileDisplay || record.sourceFileName || null;

const applyFilters = (data, filters) => {
  let result = data;
  if (filters.email?.length) result = result.filter((r) => filters.email.includes(getEmailColumnValue(r)));
  if (filters.businessName?.length) {
    result = result.filter((r) => filters.businessName.includes(getCellColumnValue(r.businessName)));
  }
  if (filters.organisationName?.length) {
    result = result.filter((r) => filters.organisationName.includes(getCellColumnValue(r.organisationName)));
  }
  if (filters.planName?.length) {
    result = result.filter((r) => filters.planName.includes(getCellColumnValue(r.planName)));
  }
  if (filters.currency?.length) {
    result = result.filter((r) => filters.currency.includes(getCellColumnValue(r.currency)));
  }
  if (filters.status?.length) {
    result = result.filter((r) => filters.status.includes(getStatusColumnValue(r)));
  }
  if (filters.fileName?.length) {
    result = result.filter((r) => filters.fileName.includes(getFileColumnValue(r) || PLACEHOLDER));
  }
  return result;
};

const applySearch = (data, query) => {
  if (!query) return data;
  const regex = new RegExp(query.replace(/\W|_/g, '[$&]'), 'i');
  return data.filter((row) => {
    const values = [
      getInviteEmail(row),
      row.firstName,
      row.lastName,
      row.businessName,
      row.organisationName,
      row.tradingName,
      row.businessRegistrationNumber,
      row.planName,
      row.currency,
      getStatusColumnValue(row),
      row.fileDisplay,
      row.sourceFileName,
      row.uploadError,
    ];
    return values.some((value) => value != null && value !== '' && String(value).match(regex));
  });
};

const SORTERS = {
  email: (a, b) => getInviteEmail(a).localeCompare(getInviteEmail(b)),
  firstName: (a, b) => (a.firstName || '').localeCompare(b.firstName || ''),
  lastName: (a, b) => (a.lastName || '').localeCompare(b.lastName || ''),
  businessName: (a, b) => (a.businessName || '').localeCompare(b.businessName || ''),
  organisationName: (a, b) => (a.organisationName || '').localeCompare(b.organisationName || ''),
  tradingName: (a, b) => (a.tradingName || '').localeCompare(b.tradingName || ''),
  businessRegistrationNumber: (a, b) =>
    (a.businessRegistrationNumber || '').localeCompare(b.businessRegistrationNumber || ''),
  planName: (a, b) => (a.planName || '').localeCompare(b.planName || ''),
  currency: (a, b) => (a.currency || '').localeCompare(b.currency || ''),
  status: (a, b) => (a.statusLabel || a.status || '').localeCompare(b.statusLabel || b.status || ''),
  lastUpdatedDate: (a, b) => (a.lastUpdatedTimestamp || 0) - (b.lastUpdatedTimestamp || 0),
  fileName: (a, b) => (a.fileDisplay || '').localeCompare(b.fileDisplay || ''),
};

const applySort = (data, key, order) => {
  if (!key || !order) return data;
  const fn = SORTERS[key];
  if (!fn) return data;
  const sorted = [...data].sort(fn);
  return order === 'descend' ? sorted.reverse() : sorted;
};

const renderCell = (value) => value || '—';

const colTitle = (short, full = short, help) => (
  <span className="bsub-th-label" title={help ? undefined : full}>
    {short}
    {help ? <HelpTip title={help} /> : null}
  </span>
);

const truncateMiddle = (text, maxLength = 28) => {
  if (!text || text.length <= maxLength) return text;
  const half = Math.floor((maxLength - 1) / 2);
  return `${text.slice(0, half)}…${text.slice(text.length - half)}`;
};

const renderFileCell = (value, record) => {
  if (!value) return '—';

  const content = (
    <span className={`assign-file-cell${record.uploadError ? ' assign-file-cell--error' : ''}`}>
      {truncateMiddle(value)}
    </span>
  );

  if (record.uploadError) {
    return (
      <Tooltip title={record.uploadError}>
        <span className="assign-upload-error">{content}</span>
      </Tooltip>
    );
  }

  if (value.length > 28) {
    return <Tooltip title={value}>{content}</Tooltip>;
  }

  return content;
};

const renderStatusBadge = (record) => {
  const status = formatInviteeStatusLabel(getInviteeBackendStatus(record));
  if (!status || status === '—') return <span className="assign-na">—</span>;

  const token = String(record.status || getInviteeBackendStatus(record)).toLowerCase();
  let mod = 'pending';
  if (token.includes('sent')) mod = 'sent';
  if (token.includes('revoked')) mod = 'revoked';
  if (token.includes('upload')) mod = 'pending';

  return <span className={`assign-invite-badge assign-invite-badge--${mod}`}>{status}</span>;
};

export default ({
  subscriptions = [],
  orgUserInvitees = [],
  availableCapacity = 0,
  sendingInviteKeys = [],
  onSendInvite,
  onBulkSendInvite,
  onEditAssignment,
  onDeleteInvitee,
  addUsersAction = null,
}) => {
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [activeFilters, setActiveFilters] = useState({});
  const [sorterKey, setSorterKey] = useState(null);
  const [sorterOrder, setSorterOrder] = useState(null);
  const [searchText, setSearchText] = useState('');

  const pending = subscriptions;

  useEffect(() => {
    setPage(1);
  }, [pending.length]);

  const filtered = useMemo(
    () => applySort(applyFilters(applySearch(pending, searchText), activeFilters), sorterKey, sorterOrder),
    [pending, searchText, activeFilters, sorterKey, sorterOrder],
  );
  const selectedFiltered = filtered.filter((record) => selectedKeys.includes(getPendingRowKey(record)));
  const selectedInvitable = selectedFiltered.filter((record) => isInvitableRow(record));
  const bulkInviteTargets = getSendableInviteTargets(selectedInvitable, availableCapacity);
  const selectedInvitableCount = selectedInvitable.length;
  const hasCapacityLimit = selectedInvitableCount > bulkInviteTargets.length;
  const selectableKeys = useMemo(
    () => filtered.filter((record) => canSelectInviteRow(record)).map((record) => getPendingRowKey(record)),
    [filtered],
  );
  const allSelectableSelected =
    selectableKeys.length > 0 && selectableKeys.every((key) => selectedKeys.includes(key));
  const someSelectableSelected =
    !allSelectableSelected && selectableKeys.some((key) => selectedKeys.includes(key));
  const isSendingInvite = (record) => sendingInviteKeys.includes(getPendingRowKey(record));

  const handleTableChange = (pagination, filters, sorter) => {
    const nextSorterKey = sorter.columnKey || null;
    const nextSorterOrder = sorter.order || null;
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(activeFilters);
    const sorterChanged = nextSorterKey !== sorterKey || nextSorterOrder !== sorterOrder;

    setActiveFilters(filters);
    setSorterKey(nextSorterKey);
    setSorterOrder(nextSorterOrder);

    if (pagination && pagination.pageSize) setPageSize(Number(pagination.pageSize));
    setPage(filtersChanged || sorterChanged ? 1 : pagination.current || 1);
  };

  const handleBulkSendInvite = async () => {
    if (!selectedInvitableCount || !onBulkSendInvite) return;

    const outcome = await onBulkSendInvite(bulkInviteTargets, {
      requestedCount: selectedInvitableCount,
      hasCapacityLimit,
    });

    if (outcome?.ok) {
      setSelectedKeys([]);
    }
  };

  const sortOrder = (key) => (sorterKey === key ? sorterOrder : false);
  const filteredValue = (key) => (activeFilters[key]?.length ? activeFilters[key] : null);

  const emailFilters = useMemo(() => toDisplayFilters(pending.map(getEmailColumnValue)), [pending]);
  const businessNameFilters = useMemo(
    () => toDisplayFilters(pending.map((r) => getCellColumnValue(r.businessName))),
    [pending],
  );
  const organisationNameFilters = useMemo(
    () => toDisplayFilters(pending.map((r) => getCellColumnValue(r.organisationName))),
    [pending],
  );
  const planNameFilters = useMemo(
    () => toDisplayFilters(pending.map((r) => getCellColumnValue(r.planName))),
    [pending],
  );
  const currencyFilters = useMemo(
    () => toDisplayFilters(pending.map((r) => getCellColumnValue(r.currency))),
    [pending],
  );
  const statusFilters = useMemo(() => toDisplayFilters(pending.map(getStatusColumnValue)), [pending]);
  const fileNameFilters = useMemo(
    () => toDisplayFilters(pending.map((r) => getFileColumnValue(r) || PLACEHOLDER)),
    [pending],
  );

  const columns = [
    {
      key: 'email',
      title: colTitle('Email'),
      dataIndex: 'email',
      sorter: true,
      sortOrder: sortOrder('email'),
      filters: emailFilters,
      filteredValue: filteredValue('email'),
      fixed: 'left',
      width: 200,
      render: (_, record) => {
        const email = getInviteEmail(record);
        return email ? <a href={`mailto:${email}`}>{email}</a> : '—';
      },
    },
    {
      key: 'firstName',
      title: colTitle('First', 'First name'),
      dataIndex: 'firstName',
      sorter: true,
      sortOrder: sortOrder('firstName'),
      width: 88,
      render: renderCell,
    },
    {
      key: 'lastName',
      title: colTitle('Last', 'Last name'),
      dataIndex: 'lastName',
      sorter: true,
      sortOrder: sortOrder('lastName'),
      width: 88,
      render: renderCell,
    },
    {
      key: 'businessName',
      title: colTitle('Business', 'Business name'),
      dataIndex: 'businessName',
      sorter: true,
      sortOrder: sortOrder('businessName'),
      filters: businessNameFilters,
      filteredValue: filteredValue('businessName'),
      width: 120,
      render: renderCell,
    },
    {
      key: 'organisationName',
      title: colTitle('Org', 'Organisation name'),
      dataIndex: 'organisationName',
      sorter: true,
      sortOrder: sortOrder('organisationName'),
      filters: organisationNameFilters,
      filteredValue: filteredValue('organisationName'),
      width: 96,
      render: renderCell,
    },
    {
      key: 'tradingName',
      title: colTitle('Trading', 'Trading name'),
      dataIndex: 'tradingName',
      sorter: true,
      sortOrder: sortOrder('tradingName'),
      width: 104,
      render: renderCell,
    },
    {
      key: 'businessRegistrationNumber',
      title: colTitle('Reg no', 'Registration number'),
      dataIndex: 'businessRegistrationNumber',
      sorter: true,
      sortOrder: sortOrder('businessRegistrationNumber'),
      width: 92,
      render: renderCell,
    },
    {
      key: 'planName',
      title: colTitle('Plan', 'Plan name'),
      dataIndex: 'planName',
      sorter: true,
      sortOrder: sortOrder('planName'),
      filters: planNameFilters,
      filteredValue: filteredValue('planName'),
      width: 96,
      render: renderCell,
    },
    {
      key: 'currency',
      title: colTitle('Curr', 'Currency'),
      dataIndex: 'currency',
      sorter: true,
      sortOrder: sortOrder('currency'),
      filters: currencyFilters,
      filteredValue: filteredValue('currency'),
      width: 72,
      render: renderCell,
    },
    {
      key: 'status',
      title: colTitle('Status', 'Invitation status', ONBOARDING_HELP.status),
      dataIndex: 'statusLabel',
      className: 'assign-status-col',
      sorter: true,
      sortOrder: sortOrder('status'),
      filters: statusFilters,
      filteredValue: filteredValue('status'),
      width: 168,
      align: 'center',
      render: (_, record) => renderStatusBadge(record),
    },
    {
      key: 'lastUpdatedDate',
      title: colTitle('Updated', 'Last updated'),
      dataIndex: 'lastUpdatedDate',
      className: 'assign-updated-col',
      sorter: true,
      sortOrder: sortOrder('lastUpdatedDate'),
      width: 108,
      render: renderCell,
    },
    {
      key: 'fileName',
      title: colTitle('File'),
      dataIndex: 'fileDisplay',
      sorter: true,
      sortOrder: sortOrder('fileName'),
      filters: fileNameFilters,
      filteredValue: filteredValue('fileName'),
      width: 132,
      className: 'assign-file-col',
      onCell: () => ({ className: 'assign-file-col' }),
      render: renderFileCell,
    },
    {
      key: 'actions',
      title: '',
      fixed: 'right',
      width: 148,
      className: 'assign-actions-col',
      onCell: () => ({ className: 'assign-actions-col' }),
      render: (_, record) => {
        const canEdit = canEditAssignment(record, orgUserInvitees);
        const canDelete = canDeleteInviteRow(record);
        const canSend = canSelectInviteRow(record);
        const sendLabel = getSendInviteButtonLabel(record);

        if (!canEdit && !canDelete && !canSend) return <span className="assign-na">—</span>;

        return (
          <div className="assign-row-actions">
            {canEdit ? (
              <Tooltip title="Edit assignment">
                <Button
                  size="small"
                  className="assign-action-btn assign-edit-btn"
                  aria-label="Edit assignment"
                  onClick={() => onEditAssignment && onEditAssignment(record)}
                >
                  <Icon type="edit" />
                </Button>
              </Tooltip>
            ) : null}
            {canDelete ? (
              <Popconfirm
                title={ONBOARDING_HELP.delete}
                onConfirm={() => onDeleteInvitee && onDeleteInvitee(record)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  size="small"
                  className="assign-action-btn assign-delete-btn"
                  aria-label="Delete user"
                >
                  <Icon type="delete" />
                </Button>
              </Popconfirm>
            ) : null}
            {canSend ? (
              <Tooltip title={sendLabel}>
                <Button
                  size="small"
                  className="assign-action-btn assign-send-invite-btn"
                  aria-label={sendLabel}
                  loading={isSendingInvite(record)}
                  onClick={() => onSendInvite && onSendInvite(record)}
                >
                  <Icon type="mail" />
                </Button>
              </Tooltip>
            ) : null}
          </div>
        );
      },
    },
  ];

  const paged = filtered;

  return (
    <div className="assign-users-wrap">
      <Alert
        className="assign-note-banner"
        message={
          <span>
            <strong>Note:</strong> This page is used to track users during the subscription onboarding process. Once a
            user signs up for a plan, a subscription will be assigned to them, and their details will be moved to the
            Subscriptions tab.
          </span>
        }
        type="info"
        banner
        showIcon={false}
      />

      <div className="assign-actions-bar">
        <div className="assign-actions-group">
          {addUsersAction}
          <Button
            className="assign-bulk-invite-btn"
            disabled={selectedInvitableCount === 0}
            onClick={handleBulkSendInvite}
          >
            <i className="fa fa-paper-plane-o" aria-hidden /> Send / Re-send ({selectedInvitableCount})
          </Button>
        </div>
        <Input
          placeholder="Search email, name, org, plan, file..."
          prefix={<Icon type="search" />}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(1);
          }}
          className="assign-table-filter"
          allowClear
        />
      </div>

      <Table
        columns={columns}
        dataSource={paged}
        rowKey={getPendingRowKey}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          columnTitle: (
            <Checkbox
              checked={allSelectableSelected}
              indeterminate={someSelectableSelected}
              disabled={!selectableKeys.length}
              onChange={(event) => {
                setSelectedKeys(event.target.checked ? selectableKeys : []);
              }}
            />
          ),
          onChange: setSelectedKeys,
          getCheckboxProps: (record) => ({
            disabled: !canSelectInviteRow(record),
          }),
        }}
        pagination={{
          current: page,
          pageSize,
          total: filtered.length,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
          hideOnSinglePage: false,
          showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total} pending assignments`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1680 }}
        locale={{
          emptyText: subscriptions.length
            ? 'No pending assignments match your search or filters.'
            : 'No pending assignments',
        }}
      />
    </div>
  );
};
